import { apiClient, getAuthToken } from '@/shared/data/client';
import { mapExecutionRunDto, mapQueryResult } from '@/shared/data/dtoMappers';
import type {
  ExecutionRunDto,
  ExecutionRunStatusSummaryDto,
  QueryFilter,
  QueryResult,
  QuerySort,
  SuiteRunStatus,
} from '@flowqa/shared';

type ExecutionRunQueryRequest = {
  page: number;
  pageSize: number;
  filters?: QueryFilter[];
  sorts?: QuerySort[];
  search?: string;
  includeDeleted?: boolean;
  includeSummary?: boolean;
  includeSummaryDetails?: boolean;
  include?: ExecutionRunInclude;
};

type ExecutionRunInclude = {
  tags?: boolean;
};

type UpdateExecutionRunInput = {
  id: string;
  name?: string | null;
  source?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  durationMs?: number | null;
  tags?: string[];
  status?: SuiteRunStatus;
};

type UpdateExecutionRunTagsInput = {
  id: string;
  tags?: string[];
};

const executionRunSortFieldMap: Readonly<Record<string, string>> = {
  resultsFileCreatedAt: 'createdAt',
  runNumber: 'startedAt',
};

const mapExecutionRunSorts = (sorts: QuerySort[]): QuerySort[] => {
  const mappedSorts: QuerySort[] = [];
  for (const sort of sorts) {
    const field = typeof sort.field === 'string' ? sort.field : String(sort.field);
    const direction = sort.direction === 'desc' ? 'desc' : 'asc';
    const mappedField = Object.prototype.hasOwnProperty.call(executionRunSortFieldMap, field)
      ? executionRunSortFieldMap[field]
      : field;
    mappedSorts.push({ field: mappedField, direction });
  }
  return mappedSorts;
};

export const queryExecutionRuns = async (
  input: {
    page: number;
    pageSize: number;
    filters?: QueryFilter[];
    sorts?: QuerySort[];
    search?: string;
    includeSummary?: boolean;
    includeSummaryDetails?: boolean;
    include?: ExecutionRunInclude;
  },
  orgId?: string | null,
): Promise<QueryResult<ExecutionRunDto>> => {
  const payload: ExecutionRunQueryRequest = {
    page: input.page,
    pageSize: input.pageSize,
    filters: input.filters,
    sorts: input.sorts ? mapExecutionRunSorts(input.sorts) : undefined,
    search: input.search,
    includeSummary: input.includeSummary,
    includeSummaryDetails: input.includeSummaryDetails,
    include: input.include,
  };
  const token = getAuthToken();
  const result = await apiClient.post<QueryResult<ExecutionRunDto>>(
    'execution-runs/query',
    payload,
    { orgId, token },
  );
  return mapQueryResult(result, mapExecutionRunDto);
};

export const queryExecutionRunStatusSummary = async (
  input: ExecutionRunQueryRequest,
  orgId?: string | null,
): Promise<ExecutionRunStatusSummaryDto> => {
  const token = getAuthToken();
  const payload: ExecutionRunQueryRequest = {
    ...input,
    sorts: input.sorts ? mapExecutionRunSorts(input.sorts) : undefined,
  };
  return await apiClient.post<ExecutionRunStatusSummaryDto>('execution-runs/summary', payload, {
    orgId,
    token,
  });
};

export const getExecutionRunById = async (
  executionRunId: string,
  orgId?: string | null,
): Promise<ExecutionRunDto> =>
  mapExecutionRunDto(
    await apiClient.get<ExecutionRunDto>(`execution-runs/${executionRunId}`, { orgId }),
  );

export const updateExecutionRun = async (
  input: UpdateExecutionRunInput,
  orgId?: string | null,
): Promise<ExecutionRunDto> => {
  const token = getAuthToken();
  return mapExecutionRunDto(
    await apiClient.patch<ExecutionRunDto>(
      `execution-runs/${input.id}`,
      {
        name: input.name,
        source: input.source,
        startedAt: input.startedAt,
        finishedAt: input.finishedAt,
        durationMs: input.durationMs,
        tags: input.tags,
        status: input.status,
      },
      { orgId, token },
    ),
  );
};

export const updateExecutionRunTags = async (
  input: UpdateExecutionRunTagsInput,
  orgId?: string | null,
): Promise<ExecutionRunDto> =>
  await updateExecutionRun(
    {
      id: input.id,
      tags: input.tags,
    },
    orgId,
  );

export const deleteExecutionRun = async (
  input: { id: string },
  orgId?: string | null,
): Promise<void> => {
  await apiClient.post<undefined>(`execution-runs/${input.id}/delete`, undefined, { orgId });
};
